import { Stack, Typography } from '@mui/material';
import React, { useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';

import {
  ApiRequest_Create_Building,
  ApiResponse_Create_Building,
  useCreateBuilding,
} from '@/api/endpoints/building/useCreateBuilding';
import { useOnboardingProgress } from '@/context/OnboardingProgressProvider';
import { usePropertyManager } from '@/context/PropertyManagerProvider';

import FormAddressAutocomplete from '../form/FormAddressAutocomplete';
import FormPropertyLocationSelect from '../form/FormPropertyLocationSelect';
import FormTextField from '../form/FormTextField';
import ModalWithForm from '../modals/ModalWithForm';

interface Props {
  propertyLocationId?: number;
  onClose: () => void;
  onBuildingCreated?: (building: ApiResponse_Create_Building) => void;
}

const BuildingCreateModal: React.FC<Props> = ({
  propertyLocationId,
  onClose,
  onBuildingCreated,
}) => {
  const { setOnboardingProgress } = useOnboardingProgress();
  const form = useForm<ApiRequest_Create_Building>({
    defaultValues: {
      property_location_id: propertyLocationId,
    },
  });
  const {
    control,
    register,
    setValue,
    watch,
    formState: { errors },
  } = form;

  const { selectedPropertyLocation } = usePropertyManager();
  const selectedPropertyLocationId = watch('property_location_id');

  const createBuildingMutation = useCreateBuilding({
    onSuccess: data => {
      setOnboardingProgress({
        hasBuilding: true,
        firstBuildingId: data.id,
      });
      onBuildingCreated?.(data);
      onClose();
    },
  });

  // Pre-populate address when property location changes
  useEffect(() => {
    if (
      selectedPropertyLocation?.id === selectedPropertyLocationId &&
      selectedPropertyLocation?.address_text
    ) {
      setValue('address_text', selectedPropertyLocation.address_text);
    }
  }, [selectedPropertyLocation, selectedPropertyLocationId, setValue]);

  const onSubmit = useCallback(
    (data: ApiRequest_Create_Building) => createBuildingMutation.mutateAsync(data),
    [createBuildingMutation]
  );

  return (
    <ModalWithForm
      title="Add a New Building"
      onClose={onClose}
      onSubmit={onSubmit}
      form={form}
      maxWidth="sm"
    >
      <Stack spacing={2} mb={2}>
        <Typography variant="body2">
          Buildings help you organize your rental units within a property location. Add details
          about this building to better manage your rental portfolio.
        </Typography>
      </Stack>

      <FormTextField
        label="Building Name (e.g. East Wing, Main House, Building A)"
        placeholder="Enter building name"
        required
        control={control}
        {...register('name', {
          required: 'Building name is required',
        })}
        size="small"
        error={!!errors.name}
        helperText={errors.name?.message?.toString()}
      />

      <FormPropertyLocationSelect
        label="Property Location this building belongs to"
        placeholder="Select property location"
        control={control}
        required
        {...register('property_location_id', {
          required: 'Property Location is required',
        })}
        size="small"
        error={!!errors.property_location_id}
        helperText={errors.property_location_id?.message?.toString()}
      />

      <FormAddressAutocomplete
        label="Building Address (e.g. 123 Main St, Miami, FL 33130)"
        placeholder="Enter building address"
        control={control}
        required
        allowManualEdit
        {...register('address_text', {
          required: 'Building address is required',
        })}
        size="small"
        error={!!errors.address_text}
        helperText={
          errors.address_text?.message?.toString() ||
          'You can add apartment/unit numbers after selecting an address'
        }
      />
    </ModalWithForm>
  );
};

export default BuildingCreateModal;
